import React from "react";
import { useForm } from "react-hook-form";
import useFormField from "../hooks/useFormField";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import Paper from "@mui/material/Paper";
import Button from "@mui/material/Button";
import Alert from "@mui/material/Alert";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import useSearchResults from "../hooks/useSearchResults";
import { describeApiError } from "../api/request";

const SearchContext = React.createContext(null);
const ResultsPaper = React.forwardRef(function ResultsPaper(
  { children, ...props },
  ref,
) {
  const search = React.useContext(SearchContext);
  return (
    <Paper
      {...props}
      ref={ref}
      data-search-results
      onMouseDown={(event) => event.preventDefault()}
    >
      {children}
      {search.error && (
        <Alert
          severity="error"
          action={<Button onClick={search.loadMore}>Retry search</Button>}
        >
          {describeApiError(search.error)}
        </Alert>
      )}
      {search.hasMore && !search.error && (
        <Button disabled={search.loading} onClick={search.loadMore}>
          Load more results
        </Button>
      )}
    </Paper>
  );
});

export default function SearchDropdown({
  value,
  onChange,
  onSelect,
  loadPage,
  pageSize,
  getKey,
  getLabel,
  renderResult,
  label,
  placeholder = "Search...",
  clearOnSelect = false,
}) {
  const form = useForm({ defaultValues: { query: "" } });
  const [internalQuery, setInternalQuery] = useFormField(form, "query");
  const query = value !== undefined ? value : internalQuery;
  const [open, setOpen] = React.useState(false);
  const search = useSearchResults({ query, enabled: open, loadPage, pageSize });
  const setQuery = onChange || setInternalQuery;
  return (
    <SearchContext.Provider value={search}>
      <ClickAwayListener
        mouseEvent="onMouseDown"
        onClickAway={() => setOpen(false)}
      >
        <Autocomplete
          className="album-search"
          clearOnBlur={false}
          disableClearable
          open={open}
          value={null}
          inputValue={query}
          options={search.results}
          filterOptions={(options) => options}
          getOptionKey={getKey}
          getOptionLabel={(option) =>
            typeof option === "string" ? option : getLabel(option)
          }
          onInputChange={(_, next, reason) => {
            if (reason === "input") {
              setQuery(next);
              setOpen(true);
            }
          }}
          onOpen={() => {
            if (query.trim()) setOpen(true);
          }}
          onClose={(event, reason) => {
            if (
              reason === "blur" &&
              event.relatedTarget?.closest("[data-search-results]")
            )
              return;
            setOpen(false);
          }}
          onChange={(_, result) => {
            if (result && typeof result !== "string") {
              setQuery(clearOnSelect ? "" : getLabel(result));
              onSelect(result);
              setOpen(false);
            }
          }}
          loading={search.loading}
          loadingText="Loading..."
          noOptionsText={search.error ? null : "No results"}
          slots={{ paper: ResultsPaper }}
          slotProps={{
            listbox: {
              sx: {
                "& .MuiAutocomplete-option": { gap: 1 },
                "& img": {
                  width: 40,
                  height: 40,
                  objectFit: "cover",
                  flexShrink: 0,
                },
              },
              className: "album-search-results",
              "aria-label": `${label} results`,
              "aria-busy": search.loading,
              onScroll: (event) => {
                const element = event.currentTarget;
                if (
                  !search.error &&
                  element.scrollTop + element.clientHeight >=
                    element.scrollHeight - 20
                )
                  search.loadMore();
              },
            },
          }}
          renderOption={(props, result) => {
            const { key, ...rest } = props;
            return (
              <li key={key} {...rest}>
                {renderResult(result)}
              </li>
            );
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={placeholder}
              slotProps={{
                ...params.slotProps,
                htmlInput: {
                  ...params.slotProps.htmlInput,
                  "aria-label": label,
                },
              }}
            />
          )}
        />
      </ClickAwayListener>
    </SearchContext.Provider>
  );
}
